import React, { useState, useEffect, useRef } from "react";
import { FaEye, FaEdit, FaTrash, FaSearch, FaPlus, FaTimes, FaImage } from "react-icons/fa";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import useAxios from "../../api/useAxios";
import Swal from 'sweetalert2';



const Brands = () => {
  const axiosInstance = useAxios();
  const [brands, setBrands] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editBrandId, setEditBrandId] = useState(null);
  const [newBrand, setNewBrand] = useState({
    name: "",
    description: "",
    image: null,
    imageFile: null,
    status: true,
  });
  const [viewBrand, setViewBrand] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const fileInputRef = useRef(null);
  const [statusFilter, setStatusFilter] = useState("all");

  // Fetch Brands
  const fetchBrands = async () => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get("/brands");
      setBrands(res.data.data);
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch brands',
        timer: 3000,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  // Sorting function
  const sortedBrands = [...brands].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Filter & Pagination
  const filteredBrands = sortedBrands.filter((b) => {
    const searchMatch = b.name.toLowerCase().includes(search.toLowerCase()) ||
      (b.description && b.description.toLowerCase().includes(search.toLowerCase()));
    
    const statusMatch = statusFilter === "all" || 
      (statusFilter === "active" && b.active) || 
      (statusFilter === "inactive" && !b.active);
    
    return searchMatch && statusMatch;
  });

  const totalEntries = filteredBrands.length;
  const totalPages = Math.ceil(totalEntries / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalEntries);
  const currentBrands = filteredBrands.slice(startIndex, endIndex);

  // Handle Sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Handle Save (Create/Update)
  const handleSaveBrand = async () => {
    if (!newBrand.name.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Brand name is required',
        timer: 3000,
        showConfirmButton: false,
      });
      return;
    }

    const formData = new FormData();
    formData.append("name", newBrand.name.trim());
    formData.append("description", newBrand.description?.trim() || "");
    formData.append("active", newBrand.status);
    
    if (newBrand.imageFile) {
      formData.append("imageUrl", newBrand.imageFile);
    }

    setIsLoading(true);
    try {
      let result;
      if (editBrandId) {
        if (!newBrand.imageFile) {
          formData.append("existingImageUrl", brands.find(b => b.id === editBrandId)?.imageUrl || "");
        }
        result = await axiosInstance.put(`/brands/${editBrandId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Brand updated successfully!',
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        result = await axiosInstance.post("/brands", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Brand created successfully!',
          timer: 3000,
          showConfirmButton: false,
        });
      }
      
      setShowModal(false);
      setEditBrandId(null);
      setNewBrand({ 
        name: "", 
        description: "", 
        image: null, 
        imageFile: null, 
        status: true 
      });
      fetchBrands();
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Error saving brand. Please try again.',
        timer: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Delete with SweetAlert
  const handleDeleteBrand = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/brands/${id}`);
        
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Brand has been deleted.',
          timer: 3000,
          showConfirmButton: false,
        });
        
        fetchBrands();
      } catch (err) {
        console.error(err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'Error deleting brand. Please try again.',
          timer: 3000,
        });
      }
    }
  };

  // Toggle Status using the new PATCH endpoint
  const handleToggleStatus = async (brand) => {
    const newStatus = !brand.active;
    
    const result = await Swal.fire({
      title: 'Change Status',
      text: `Are you sure you want to ${newStatus ? 'activate' : 'deactivate'} ${brand.name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, ${newStatus ? 'activate' : 'deactivate'} it`,
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        // Using the new PATCH endpoint for status update
        const requestData = { active: newStatus };
        const response = await axiosInstance.patch(`/brands/${brand.id}/status`, requestData);
        console.log(response.data)
        
        // Show success message
        await Swal.fire({
          icon: 'success',
          title: 'Status Updated!',
          text: `Brand has been ${newStatus ? 'activated' : 'deactivated'} successfully.`,
          timer: 3000,
          showConfirmButton: false,
        });
        
        // Update the local state immediately for better UX
        setBrands(prevBrands => 
          prevBrands.map(b => 
            b.id === brand.id 
              ? { ...b, active: newStatus } 
              : b
          )
        );
        
      } catch (err) {
        console.error(err);
        
        // Try fallback to PUT method if PATCH fails (for backward compatibility)
        try {
          const formData = new FormData();
          formData.append("name", brand.name);
          formData.append("description", brand.description || "");
          formData.append("active", newStatus);
          formData.append("existingImageUrl", brand.imageUrl || "");
          
          await axiosInstance.put(`/brands/${brand.id}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          
          setBrands(prevBrands => 
            prevBrands.map(b => 
              b.id === brand.id 
                ? { ...b, active: newStatus } 
                : b
            )
          );
          
          Swal.fire({
            icon: 'success',
            title: 'Status Updated!',
            text: `Brand has been ${newStatus ? 'activated' : 'deactivated'} successfully.`,
            timer: 3000,
            showConfirmButton: false,
          });
          
        } catch (fallbackErr) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: fallbackErr.response?.data?.message || 'Error updating brand status. Please try again.',
            timer: 3000,
          });
        }
      }
    }
  };

  // Handle image upload with validation
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: 'warning',
          title: 'File Too Large',
          text: 'Image size should be less than 5MB',
          timer: 3000,
          showConfirmButton: false,
        });
        return;
      }
      
      if (!file.type.match('image.*')) {
        Swal.fire({
          icon: 'warning',
          title: 'Invalid File',
          text: 'Please select a valid image file (JPG, PNG, etc.)',
          timer: 3000,
          showConfirmButton: false,
        });
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewBrand({
          ...newBrand,
          image: reader.result,
          imageFile: file
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Reset modal
  const resetModal = () => {
    setNewBrand({ 
      name: "", 
      description: "", 
      image: null, 
      imageFile: null, 
      status: true 
    });
    setEditBrandId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Open edit modal with brand data
  const openEditModal = (brand) => {
    setNewBrand({
      name: brand.name,
      description: brand.description,
      image: `http://localhost:8080/brands/${brand.imageUrl}`,
      imageFile: null,
      status: brand.active,
    });
    setEditBrandId(brand.id);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setShowModal(true);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return "N/A";
    }
  };

  // Get image URL for display
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return `https://api.adanapaints.com/uploads/brands/${imageUrl}`;
    return `https://api.adanapaints.com/uploads/brands/${imageUrl}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      {/* HEADER */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Brand Management</h1>
        <p className="text-gray-600">Manage your brand details, status, and images</p>
      </div>

      {/* CONTROLS CARD */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col md:flex-row gap-4 flex-1">
            {/* SEARCH */}
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search brands by name or description..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {/* STATUS FILTER */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="appearance-none px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white pr-10"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                </select>
                <IoMdArrowDropdown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
              </div>
            </div>

            {/* ROWS PER PAGE */}
            <div className="flex items-center gap-3">
              <span className="text-gray-600 whitespace-nowrap">Show</span>
              <div className="relative">
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (value > 0) {
                      setRowsPerPage(value);
                      setCurrentPage(1);
                    }
                  }}
                  className="appearance-none px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white pr-10"
                >
                  {[5, 10, 20, 50].map((num) => (
                    <option key={num} value={num}>
                      {num} rows
                    </option>
                  ))}
                </select>
                <IoMdArrowDropdown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* ADD BUTTON */}
          <button
            onClick={() => {
              resetModal();
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg whitespace-nowrap"
          >
            <FaPlus />
            <span>Add New Brand</span>
          </button>
        </div>
      </div>

      {/* STATUS SUMMARY */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-blue-500">
          <div className="text-sm text-gray-500">Total Brands</div>
          <div className="text-2xl font-bold text-gray-800">{brands.length}</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-green-500">
          <div className="text-sm text-gray-500">Active Brands</div>
          <div className="text-2xl font-bold text-gray-800">
            {brands.filter(b => b.active).length}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-red-500">
          <div className="text-sm text-gray-500">Inactive Brands</div>
          <div className="text-2xl font-bold text-gray-800">
            {brands.filter(b => !b.active).length}
          </div>
        </div>
      </div>

      {/* MAIN TABLE CARD */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
        {/* TABLE HEADER */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">Brand Details</h2>
          <div className="text-sm text-gray-600 mt-1">
            {statusFilter === "all" ? "Showing all brands" : 
             statusFilter === "active" ? "Showing active brands only" : 
             "Showing inactive brands only"}
          </div>
        </div>

        {/* TABLE CONTAINER */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="py-20 flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th 
                    className="px-6 py-4 text-left font-semibold text-gray-700 cursor-pointer"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-2">
                      Brand Name
                      {sortConfig.key === 'name' && (
                        sortConfig.direction === 'asc' ? <IoMdArrowDropup /> : <IoMdArrowDropdown />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-gray-700">Logo</th>
                  <th className="px-6 py-4 text-left font-semibold text-gray-700">Description</th>
                  <th className="px-6 py-4 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-4 text-left font-semibold text-gray-700">Created</th>
                  <th className="px-6 py-4 text-center font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentBrands.map((brand, index) => (
                  <tr 
                    key={brand.id} 
                    className={`border-b border-gray-100 hover:bg-blue-50 transition-colors ${
                      !brand.active ? "bg-red-50 hover:bg-red-100" : ""
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{brand.name}</div>
                        <div className="text-sm text-gray-500">ID: {brand.id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="relative">
                        <img
                          src={getImageUrl(brand.imageUrl)}
                          alt={brand.name}
                          className="h-12 w-12 rounded-lg object-cover border border-gray-200 shadow-sm"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/48';
                          }}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="max-w-xs">
                        <p className="text-gray-700 line-clamp-2">
                          {brand.description || "No description"}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleToggleStatus(brand)}
                          className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            brand.active 
                              ? "bg-green-500 hover:bg-green-600 focus:ring-green-500" 
                              : "bg-gray-300 hover:bg-gray-400 focus:ring-gray-400"
                          }`}
                        >
                          <span
                            className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                              brand.active ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          brand.active 
                            ? "bg-green-100 text-green-800" 
                            : "bg-red-100 text-red-800"
                        }`}>
                          {brand.active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600">
                        {formatDate(brand.createAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => {
                            setViewBrand(brand);
                            setShowViewModal(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <FaEye size={18} />
                        </button>
                        <button
                          onClick={() => openEditModal(brand)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Edit Brand"
                        >
                          <FaEdit size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteBrand(brand.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Brand"
                        >
                          <FaTrash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* EMPTY STATE */}
          {!isLoading && currentBrands.length === 0 && (
            <div className="py-16 text-center">
              <div className="text-gray-400 mb-4">
                <FaSearch size={48} className="mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                No brands found
              </h3>
              <p className="text-gray-500">
                {search || statusFilter !== "all" 
                  ? "Try adjusting your search or filter terms" 
                  : "Add your first brand to get started"}
              </p>
            </div>
          )}
        </div>

        {/* PAGINATION */}
        {totalEntries > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-600">
                Showing <span className="font-semibold">{startIndex + 1}</span> to{" "}
                <span className="font-semibold">{endIndex}</span> of{" "}
                <span className="font-semibold">{totalEntries}</span> brands
              </div>
              
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-lg border transition-colors ${
                      currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                    }`}
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={i}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1 rounded transition-colors ${
                            currentPage === pageNum
                              ? "bg-blue-600 text-white"
                              : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 rounded-lg border transition-colors ${
                      currentPage === totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ADD/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-scale-in">
            {/* MODAL HEADER */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-800">
                  {editBrandId ? "Edit Brand" : "Add New Brand"}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetModal();
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            {/* MODAL BODY */}
            <div className="p-6 space-y-4">
              {/* BRAND NAME */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter brand name"
                  value={newBrand.name}
                  onChange={(e) => setNewBrand({ ...newBrand, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
              </div>

              {/* DESCRIPTION */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  placeholder="Enter brand description (optional)"
                  value={newBrand.description}
                  onChange={(e) => setNewBrand({ ...newBrand, description: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                />
              </div>

              {/* IMAGE UPLOAD */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand Logo
                  {editBrandId && (
                    <span className="text-xs text-gray-500 ml-2">
                      (Upload new image only if you want to change it)
                    </span>
                  )}
                </label>
                <div className="space-y-3">
                  {/* Preview - Always show when editing */}
                  {editBrandId && !newBrand.imageFile && (
                    <div className="relative">
                      <img
                        src={newBrand.image || getImageUrl(brands.find(b => b.id === editBrandId)?.imageUrl)}
                        alt="Current logo"
                        className="h-24 w-24 rounded-lg object-cover border border-gray-200 shadow-sm"
                      />
                      <div className="mt-2 text-xs text-green-600 font-medium">
                        Current logo - upload new to replace
                      </div>
                    </div>
                  )}
                  
                  {/* New image preview */}
                  {newBrand.imageFile && (
                    <div className="relative">
                      <img
                        src={newBrand.image}
                        alt="New logo preview"
                        className="h-24 w-24 rounded-lg object-cover border-2 border-blue-500 shadow-sm"
                      />
                      <div className="mt-2 text-xs text-blue-600 font-medium">
                        New logo preview
                      </div>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <label className="flex flex-col items-center justify-center w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer">
                    <FaImage className="text-gray-400 mb-2" size={24} />
                    <span className="text-sm text-gray-600">
                      {newBrand.imageFile ? "Change Image" : 
                       editBrandId ? "Upload New Image (Optional)" : 
                       "Click to upload image"}
                    </span>
                    <span className="text-xs text-gray-500 mt-1">
                      PNG, JPG up to 5MB
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* STATUS */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={newBrand.status}
                  onChange={(e) =>
                    setNewBrand({ ...newBrand, status: e.target.value === "true" })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>

            {/* MODAL FOOTER */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetModal();
                  }}
                  className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBrand}
                  disabled={isLoading || !newBrand.name.trim()}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : editBrandId ? (
                    "Update Brand"
                  ) : (
                    "Create Brand"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW MODAL */}
      {showViewModal && viewBrand && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-scale-in">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-800">Brand Details</h3>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex flex-col items-center mb-6">
                <img
                  src={getImageUrl(viewBrand.imageUrl)}
                  alt={viewBrand.name}
                  className="h-32 w-32 rounded-lg object-cover border-4 border-white shadow-lg"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/128';
                  }}
                />
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">ID</label>
                    <p className="text-gray-900 font-medium">{viewBrand.id}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Name</label>
                    <p className="text-gray-900 font-medium">{viewBrand.name}</p>
                  </div>
                </div>
                
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Description</label>
                  <p className="text-gray-700 mt-1 p-3 bg-gray-50 rounded-lg">
                    {viewBrand.description || "No description provided"}
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        viewBrand.active 
                          ? "bg-green-100 text-green-800" 
                          : "bg-red-100 text-red-800"
                      }`}>
                        {viewBrand.active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Created</label>
                    <p className="text-gray-700">
                      {formatDate(viewBrand.createAt)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Brands;